import {
  Globe,
  Users,
  Megaphone,
  Shield,
  BookOpen,
  CreditCard,
  Settings,
  LogOut,
  ChevronsUpDown,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback } from "react";
import { mutate } from "swr";
import Image from "next/image";
import logoImg from "@/public/logo-no-text.svg";
import useCurrentAccount from "@/components/context/useCurrentAccount";
import { cn } from "@/lib/utils";
import { ThemeDropdown } from "@/components/ui/theme-dropdown";
import { Button } from "@/components/ui/Button";

export function AppSidebar({
  activePath,
  className,
}: {
  activePath: string;
  className?: string;
}) {
  const router = useRouter();
  const { account } = useCurrentAccount({ requireLoggedIn: true });

  const logoutUser = useCallback(
    (e: any) => {
      e.preventDefault();

      fetch("/web_api/accounts/logout", {
        method: "POST",
      })
        .then((resp) => resp.json())
        .then((json) => {
          if (json.status === "ok") {
            router.push("/").then(() => mutate("/web_api/accounts/current"));
          }
        })
        .catch((e) => {
          console.error(e);
        });
    },
    [router],
  );

  const mainNavigation = [
    {
      name: "Monitors",
      href: "/dashboard/monitors",
      icon: Globe,
      badge: account?.down_count || null,
    },
    {
      name: "Contacts",
      href: "/dashboard/contacts",
      icon: Users,
    },
    {
      name: "Status Pages",
      href: "/dashboard/status_pages",
      icon: Megaphone,
    },
  ];

  const bottomNavigation = [
    {
      name: "Documentation",
      href: "/docs",
      icon: BookOpen,
      external: true,
    },
    {
      name: "Billing",
      href: "/dashboard/billing",
      icon: CreditCard,
    },
    {
      name: "Settings",
      href: "/dashboard/settings",
      icon: Settings,
    },
  ];

  return (
    <div
      className={cn(
        "hidden md:flex h-screen sticky top-0 flex-col w-64 bg-sidebar-background border-r border-sidebar-border flex-shrink-0",
        className,
      )}
    >
      {/* Header */}
      <div className="p-3">
        <Link
          href="/"
          className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-accent"
        >
          <div className="flex h-8 w-8 items-center justify-center">
            <Image
              width={32}
              height={32}
              className="h-8 w-8"
              src={logoImg}
              alt="Uptime Monitor"
            />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-semibold">Uptime Monitor</p>
            <p className="text-xs text-muted-foreground truncate">
              {account?.email || "Loading..."}
            </p>
          </div>
          {/* <ChevronsUpDown className="h-4 w-4 text-muted-foreground" /> */}
        </Link>
      </div>

      {/* Main Navigation */}
      <div className="flex-1 px-3">
        <div className="space-y-1">
          {mainNavigation.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                activePath === item.href
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              )}
            >
              <item.icon className="h-4 w-4" />
              <span className="flex-1">{item.name}</span>
              {item.badge && item.badge > 0 && (
                <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-destructive px-1 text-xs text-destructive-foreground">
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
        </div>

        {account?.is_admin && (
          <>
            <div className="my-3 h-px bg-border" />
            <div className="space-y-1">
              <Link
                href="/admin/campaigns"
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  activePath.startsWith("/admin")
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )}
              >
                <Shield className="h-4 w-4" />
                <span>Admin</span>
              </Link>
            </div>
          </>
        )}

        <div className="my-3 h-px bg-border" />

        <div className="space-y-1">
          {bottomNavigation.map((item) =>
            item.external ? (
              <a
                key={item.href}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <item.icon className="h-4 w-4" />
                <span>{item.name}</span>
              </a>
            ) : (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  activePath === item.href
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                )}
              >
                <item.icon className="h-4 w-4" />
                <span>{item.name}</span>
              </Link>
            ),
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 space-y-1">
        <ThemeDropdown />
        <div className="h-px bg-border my-2" />
        <Button
          onClick={logoutUser}
          variant="ghost"
          className="w-full justify-start gap-3 px-3 text-muted-foreground hover:text-accent-foreground"
          aria-label="Log Out"
        >
          <LogOut className="h-4 w-4" />
          <span>Log Out</span>
        </Button>
      </div>
    </div>
  );
}
